import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, LinearProgress, Tooltip, Typography } from "@mui/material";
import { MdHelpOutline } from "react-icons/md";
import { buildTours } from "./tourContent";
import { readStoredUser } from "../menuRoutes";
import TourDirectory from "./TourDirectory";
import { completeTour, getTourProgress, isWelcomeSeen, markWelcomeSeen, resetTour, saveTourProgress } from "./tourStorage";

// Guided tour for the warehouse portal.
//   - useTour().openDirectory()  opens the list of tours (search, start, resume, restart)
//   - useTour().start(id)        starts one tour: "full", "welcome" or "page:<route>"
// Progress is saved per user (tourStorage.js). The content depends on the login and the menus it received
// (tourContent.js). If anything in the tour fails to build, the application itself is not affected.

const TourContext = createContext({ start: () => {}, stop: () => {}, openDirectory: () => {}, active: false });
export const useTour = () => useContext(TourContext);

const GOLD = "rgb(255, 209, 2)";
const EMERALD = "rgb(255, 209, 2)"; // main button colour in this panel
const ON = "#111827";
const CONTENT = "main";
const DEV = process.env.NODE_ENV !== "production";

const userMenus = () => {
  const u = readStoredUser();
  return u && Array.isArray(u.menus) ? u.menus : [];
};

const visible = (el) => {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== "hidden";
};

const findHeading = () => {
  const root = document.querySelector(CONTENT);
  if (!root) return null;
  const all = [...root.querySelectorAll("h1, h2, h3, h4, h5, h6")].filter(visible);
  return all[0] || [...root.children].find(visible) || null;
};

// a control on the page, found by what it says on screen (used when a step has no data-tour marker)
const findOne = (label) => {
  const root = document.querySelector(CONTENT);
  if (!root || !label) return null;
  const want = label.toLowerCase();
  const nodes = [...root.querySelectorAll("button, a, [role='tab'], [role='button'], th, td, input, select, .MuiChip-root, [title], [aria-label], h1, h2, h3, h4, h5, h6, p, span, div")]
    .filter((el) => visible(el) && el.children.length < 6);
  const text = (el) => (el.innerText || "").replace(/\s+/g, " ").trim().toLowerCase();
  const attrs = (el) => `${el.getAttribute("placeholder") || ""} ${el.getAttribute("title") || ""} ${el.getAttribute("aria-label") || ""}`.toLowerCase();
  const exact = nodes.find((el) => text(el) === want || attrs(el).trim() === want);
  if (exact) return exact;
  const hits = nodes.filter((el) => text(el).includes(want) || attrs(el).includes(want));
  hits.sort((a, b) => a.getBoundingClientRect().width * a.getBoundingClientRect().height - b.getBoundingClientRect().width * b.getBoundingClientRect().height);
  return hits[0] || null;
};

// the first of the given labels that is on screen
const findByLabel = (labels) => {
  for (const l of [].concat(labels || [])) {
    const el = findOne(l);
    if (el) return el;
  }
  return null;
};

// the stable data-tour marker first; the text on screen is the fallback
const findTarget = (step) => {
  if (step.target) {
    const el = document.querySelector(step.target);
    if (visible(el)) return el;
    if (!step.find) return null;
  }
  return step.kind === "page" ? findHeading() : findByLabel(step.find);
};

const CENTRED = new Set(["intro", "section", "end"]);
const SKIPPABLE = new Set(["point", "ui"]);

export function TourProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [active, setActive] = useState(null); // { id, title, steps }
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [ready, setReady] = useState(false);
  const [directoryOpen, setDirectoryOpen] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const dir = useRef(1);
  const targetEl = useRef(null);
  const nextBtn = useRef(null);

  const reduceMotion = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const mobile = typeof window !== "undefined" && window.innerWidth < 600;

  // every tour this login can run (rebuilt when the page changes, so menus that arrive late are picked up)
  const built = useMemo(() => {
    try {
      return buildTours(userMenus());
    } catch (e) {
      if (DEV) console.warn("[tour] could not build tours", e);
      return { roleName: "", group: "", tours: [] };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
  const tours = built.tours;

  const stop = useCallback(() => {
    setActive(null);
    setRect(null);
    targetEl.current = null;
  }, []);

  const start = useCallback((tourId = "full", opts = {}) => {
    const tour = tours.find((t) => t.id === tourId);
    if (!tour || tour.steps.length === 0) return;
    if (opts.restart) resetTour(tourId);
    const steps = [...tour.steps, { kind: "end", title: "Tour complete", text: "" }];
    let from = 0;
    if (opts.resume) {
      const saved = getTourProgress(tourId);
      if (saved && saved.status === "in_progress") from = Math.min(saved.currentStep, steps.length - 2);
    }
    dir.current = 1;
    setRect(null);
    setReady(true);
    setIdx(from);
    setActive({ id: tour.id, title: tour.title, steps });
    setDirectoryOpen(false);
    setWelcomeOpen(false);
  }, [tours]);

  const openDirectory = useCallback(() => setDirectoryOpen(true), []);

  // welcome screen the first time this user signs in
  useEffect(() => {
    if (!sessionStorage.getItem("token") || location.pathname === "/login") return undefined;
    if (isWelcomeSeen()) return undefined;
    const t = setTimeout(() => setWelcomeOpen(true), 1500);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const step = active ? active.steps[idx] : null;

  const go = useCallback((d) => {
    dir.current = d;
    setIdx((i) => (active ? Math.min(Math.max(i + d, 0), active.steps.length - 1) : i));
  }, [active]);

  // save progress; reaching the end marks the tour completed
  useEffect(() => {
    if (!active || !step) return;
    const total = active.steps.length - 1;
    if (step.kind === "end") completeTour(active.id, total);
    else if (idx > 0) saveTourProgress(active.id, { currentStep: idx, totalSteps: total });
  }, [active, step, idx]);

  // move to the step's page, then find and measure its target
  useEffect(() => {
    if (!step || step.kind === "end") return undefined;
    targetEl.current = null;
    setRect(null);
    if (step.path && `${location.pathname}${location.search}` !== step.path) {
      navigate(step.path);
      return undefined; // runs again once the location has changed
    }
    if (CENTRED.has(step.kind) || (step.kind === "point" && !step.target && !step.find)) {
      setReady(true);
      return undefined;
    }
    setReady(false);
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      const el = findTarget(step);
      if (el) {
        clearInterval(timer);
        targetEl.current = el;
        const r = el.getBoundingClientRect();
        if (r.top < 90 || r.bottom > window.innerHeight - (mobile ? 260 : 20)) {
          el.scrollIntoView({ block: mobile ? "start" : "center", behavior: reduceMotion ? "auto" : "smooth" });
        }
        setReady(true);
        setRect(el.getBoundingClientRect());
      } else if (tries > 24) {
        clearInterval(timer);
        if (DEV) console.warn("[tour] target not found:", step.target || step.find || step.title);
        if (SKIPPABLE.has(step.kind) && !step.centerIfMissing) {
          // control not on this page for this user: skip it
          const next = Math.min(Math.max(idx + dir.current, 0), active.steps.length - 1);
          if (next === idx) setReady(true); else setIdx(next);
        } else {
          setReady(true); // page loaded but no heading found: show the card in the middle
        }
      }
    }, 150);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, location.pathname, location.search, navigate]);

  // keep the highlight on the element while the page scrolls or resizes
  useEffect(() => {
    if (!active) return undefined;
    const update = () => { if (targetEl.current && document.contains(targetEl.current)) setRect(targetEl.current.getBoundingClientRect()); };
    const t = setInterval(update, 200);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => { clearInterval(t); window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  }, [active]);

  // keyboard
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag)) return;
      if (e.key === "Escape") stop();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, go, stop]);

  // move keyboard focus to the Next button for each new step
  useEffect(() => {
    if (ready && nextBtn.current) nextBtn.current.focus({ preventScroll: true });
  }, [ready, idx, active]);

  // "Help for this page"
  const pageTour = useMemo(
    () => tours.find((t) => t.id === `page:${location.pathname}`) || tours.find((t) => t.match && t.match.test(location.pathname)) || null,
    [tours, location.pathname]
  );

  const value = useMemo(() => ({ start, stop, openDirectory, active: !!active }), [start, stop, openDirectory, active]);

  const W = 360;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const centred = !step || CENTRED.has(step.kind);
  let cardStyle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  if (mobile && !centred) {
    cardStyle = { left: 0, right: 0, bottom: 0, top: "auto", width: "100%", maxWidth: "100%", borderRadius: "16px 16px 0 0" };
  } else if (rect && !centred) {
    const left = Math.min(Math.max(rect.left, 16), vw - W - 16);
    const below = rect.bottom + 16 + 230 < vh;
    cardStyle = below ? { top: rect.bottom + 16, left } : { top: Math.max(16, rect.top - 16 - 230), left };
  }
  const total = active ? active.steps.length - 1 : 0;
  const last = active ? idx >= total - 1 : false;
  const progress = total > 0 ? Math.round((idx / total) * 100) : 0;
  const isEnd = step && step.kind === "end";
  const motion = reduceMotion ? "none" : undefined;

  return (
    <TourContext.Provider value={value}>
      {children}

      {/* welcome, first sign-in */}
      <Dialog open={welcomeOpen && !active} onClose={() => { markWelcomeSeen(); setWelcomeOpen(false); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "20px" }, "aria-label": "Welcome" }}>
        <DialogContent sx={{ textAlign: "center", pt: 4 }}>
          <Box sx={{ width: 64, height: 64, mx: "auto", mb: 1.5, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#fef9c3", color: ON, border: `3px solid ${GOLD}` }}>
            <MdHelpOutline size={34} />
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: 22 }}>Welcome to your seller portal!</Typography>
          <Typography sx={{ fontSize: 13.5, color: "#475569", mt: 1, lineHeight: 1.6 }}>
            This short guided tour explains your menus, what each page is for and what its buttons do. You can skip it now and start it again any time from the Help button at the top.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", gap: 1, px: 3, pb: 3, "& > :not(style) ~ :not(style)": { ml: 0 } }}>
          <Button fullWidth variant="contained" disableElevation onClick={() => { markWelcomeSeen(); start("full", { restart: true }); }} sx={{ textTransform: "none", fontWeight: 700, py: 1, bgcolor: EMERALD, color: ON, "&:hover": { bgcolor: "rgb(235, 192, 0)" } }}>Start Guided Tour</Button>
          <Button fullWidth variant="outlined" onClick={() => { markWelcomeSeen(); setWelcomeOpen(false); setDirectoryOpen(true); }} sx={{ textTransform: "none", fontWeight: 700, color: ON, borderColor: EMERALD }}>Explore Modules</Button>
          <Button fullWidth onClick={() => { markWelcomeSeen(); setWelcomeOpen(false); }} sx={{ textTransform: "none", color: "#64748b" }}>Skip for Now</Button>
        </DialogActions>
      </Dialog>

      <TourDirectory open={directoryOpen} onClose={() => setDirectoryOpen(false)} tours={tours} roleName={built.roleName} onStart={start} />

      {/* small contextual help on pages that have a tour */}
      {!active && !welcomeOpen && !directoryOpen && pageTour && location.pathname !== "/login" && (
        <Tooltip title="Help for this page" placement="left">
          <IconButton
            aria-label="Help for this page"
            onClick={() => start(pageTour.id, { restart: true })}
            sx={{ position: "fixed", right: 20, bottom: 20, zIndex: 1200, width: 44, height: 44, bgcolor: EMERALD, color: ON, border: "2px solid #111827", boxShadow: "0 8px 22px rgba(15,23,42,0.3)", "&:hover": { bgcolor: "rgb(235, 192, 0)" } }}
          >
            <MdHelpOutline size={24} />
          </IconButton>
        </Tooltip>
      )}

      {active && step && !isEnd && (
        <>
          {/* blocks clicks on the page while the tour is open */}
          <Box sx={{ position: "fixed", inset: 0, zIndex: 1400, background: rect ? "transparent" : "rgba(15,23,42,0.6)" }} />

          {rect && (
            <Box
              sx={{
                position: "fixed", zIndex: 1401, pointerEvents: "none", borderRadius: "10px",
                top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12,
                boxShadow: "0 0 0 9999px rgba(15,23,42,0.6)", outline: `3px solid ${GOLD}`,
                transition: motion || "all .28s cubic-bezier(.22,1,.36,1)",
              }}
            />
          )}

          {ready && (
            <Box
              key={`${active.id}-${idx}`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="tour-title"
              aria-describedby="tour-text"
              sx={{
                position: "fixed", zIndex: 1402, width: W, maxWidth: "calc(100vw - 32px)", p: 2.25, borderRadius: "16px", bgcolor: "#fff",
                boxShadow: "0 22px 60px rgba(6,40,32,0.35)", ...cardStyle,
                animation: motion || "tourCard .25s ease both", "@keyframes tourCard": { from: { opacity: 0 }, to: { opacity: 1 } },
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#a16207" }}>
                  {step.group || active.title}{step.kind === "section" ? " · menu" : step.page ? ` · page ${step.page} of ${step.pages}` : ""}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }} aria-live="polite">Step {idx + 1} of {total}</Typography>
              </Box>
              <Typography id="tour-title" sx={{ fontSize: 17, fontWeight: 800, color: "#0F1729", mt: 0.25 }}>
                {step.heading || (step.kind === "point" ? [].concat(step.find || step.title)[0] : step.title)}
              </Typography>
              {step.kind === "point" && !step.task && <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mt: 0.1 }}>on {step.title}</Typography>}
              <Typography id="tour-text" sx={{ fontSize: 13.5, color: "#334155", mt: 0.75, lineHeight: 1.55 }}>{step.text}</Typography>

              <LinearProgress variant="determinate" value={progress} aria-label="Tour progress" sx={{ mt: 1.75, height: 4, borderRadius: 2, bgcolor: "#f1f5f9", "& .MuiLinearProgress-bar": { bgcolor: EMERALD } }} />
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1.25 }}>
                <Button size="small" onClick={stop} sx={{ textTransform: "none", color: "#64748b" }}>Skip tour</Button>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {idx > 0 && <Button size="small" variant="outlined" onClick={() => go(-1)} sx={{ textTransform: "none", color: "#334155", borderColor: "#cbd5e1" }}>Previous</Button>}
                  <Button ref={nextBtn} size="small" variant="contained" disableElevation onClick={() => go(1)} sx={{ textTransform: "none", fontWeight: 700, bgcolor: EMERALD, color: ON, "&:hover": { bgcolor: "rgb(235, 192, 0)" } }}>
                    {last ? "Finish" : idx === 0 ? "Start" : "Next"}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </>
      )}

      {/* completion */}
      <Dialog open={!!active && isEnd} onClose={stop} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "20px" }, "aria-label": "Tour complete" }}>
        <DialogContent sx={{ textAlign: "center", pt: 4 }}>
          <Typography sx={{ fontSize: 44, lineHeight: 1 }} aria-hidden="true">🎉</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 22, mt: 1 }}>Congratulations!</Typography>
          <Typography sx={{ fontSize: 13.5, color: "#475569", mt: 1, lineHeight: 1.6 }}>
            {active && active.completion ? active.completion : "You have completed this tour. You now know more about the pages and buttons available to you."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", gap: 1, px: 3, pb: 3, "& > :not(style) ~ :not(style)": { ml: 0 } }}>
          <Button fullWidth variant="contained" disableElevation onClick={stop} sx={{ textTransform: "none", fontWeight: 700, bgcolor: EMERALD, color: ON, "&:hover": { bgcolor: "rgb(235, 192, 0)" } }}>Finish</Button>
          <Button fullWidth variant="outlined" onClick={() => { stop(); setDirectoryOpen(true); }} sx={{ textTransform: "none", fontWeight: 700, color: ON, borderColor: EMERALD }}>Explore Another Module</Button>
          <Button fullWidth onClick={() => active && start(active.id, { restart: true })} sx={{ textTransform: "none", color: "#64748b" }}>Restart Tour</Button>
        </DialogActions>
      </Dialog>
    </TourContext.Provider>
  );
}
